import { useRef } from "react";
import { SplitText, gsap, useGSAP } from "../../animations/gsapSetup.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./AiHero.module.css";

// AI gradient value (matches --color-ai-gradient in variables.css)
const AI_GRADIENT = "linear-gradient(135deg, #0071e3, #6e3eff)";

export function AiHero() {
  const reducedMotion = useReducedMotion();
  const { t } = useI18n();
  const sectionRef = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = sectionRef.current;
      if (!el) return;

      const wordmark = el.querySelector(`.${styles.wordmark}`) as HTMLElement;
      const subtitle = el.querySelector(`.${styles.subtitle}`) as HTMLElement;

      if (!wordmark || !subtitle) return;

      if (reducedMotion) {
        gsap.set(wordmark, { autoAlpha: 1 });
        gsap.set(subtitle, { autoAlpha: 1 });
        return;
      }

      const glowOrb = el.querySelector(`.${styles.glowOrb}`);
      const bgLayer1 = el.querySelector(`.${styles.bgLayer1}`);

      // SplitText for kinetic typography
      const wordmarkSplit = SplitText.create(wordmark, { type: "chars" });
      const subtitleSplit = SplitText.create(subtitle, { type: "words" });

      // Fix: gradient-clip-text breaks when SplitText wraps chars in child divs.
      // Remove gradient from parent, apply directly to each char element.
      wordmark.style.background = "none";
      wordmark.style.webkitTextFillColor = "";
      wordmark.style.color = "#fff";
      for (const c of wordmarkSplit.chars) {
        if (c instanceof HTMLElement) {
          c.style.backgroundImage = AI_GRADIENT;
          c.style.backgroundClip = "text";
          c.style.webkitBackgroundClip = "text";
          c.style.webkitTextFillColor = "transparent";
          c.style.display = "inline-block";
        }
      }

      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

      tl.set(wordmark, { autoAlpha: 1 })
        .from(wordmarkSplit.chars, {
          y: "110%",
          rotateX: -45,
          opacity: 0,
          filter: "blur(6px)",
          duration: 0.9,
          stagger: 0.04,
          onComplete: () => {
            for (const c of wordmarkSplit.chars) {
              if (c instanceof HTMLElement) c.style.filter = "";
            }
          },
        })
        .set(subtitle, { autoAlpha: 1 }, "-=0.3")
        .from(subtitleSplit.words, { y: 30, opacity: 0, duration: 0.7, stagger: 0.05 }, "-=0.3");

      // Glow orb: breathing + scroll-linked
      if (glowOrb) {
        gsap.to(glowOrb, {
          scale: 1.2,
          opacity: 0.6,
          duration: 2,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
        gsap.to(glowOrb, {
          yPercent: -40,
          scale: 1.5,
          scrollTrigger: { trigger: el, start: "top top", end: "bottom top", scrub: 1 },
        });
      }

      // Parallax background
      if (bgLayer1) {
        gsap.to(bgLayer1, {
          yPercent: -25,
          scrollTrigger: { trigger: el, start: "top top", end: "bottom top", scrub: 1 },
        });
      }
    },
    {
      scope: sectionRef,
      // 言語切替時の再アニメーション:
      // useGSAPはdependencies変更時にコンテキストを自動revert(SplitTextのDOM復元含む)
      // → 新テキストで再split → 再アニメーション。
      // これがないと旧言語のSplitText DOMが残り、テキスト描画が壊れる。
      dependencies: [t.ai.heroWordmark, t.ai.heroSubtitle, reducedMotion],
    },
  );

  return (
    <section ref={sectionRef} className={styles.hero}>
      <div className={styles.bg}>
        <div className={styles.bgLayer1} />
        <div className={styles.glowOrb} />
      </div>
      <div className={styles.content}>
        <h1 className={styles.wordmark} style={{ visibility: "hidden" }}>
          {t.ai.heroWordmark.split("\n").map((line, i, arr) => (
            <span key={line}>
              {line}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
        </h1>
        <p className={styles.subtitle} style={{ visibility: "hidden" }}>
          {t.ai.heroSubtitle}
        </p>
      </div>
    </section>
  );
}
